import { createContext, useContext } from 'react'
import { ActionDispatcher, JourneyReducerElements, JourneyState } from './types/journey-context'

/**
 * Enrichment is the async hydration of step display data after the journey loads.
 *
 * The API returns action steps with only a `notification_id` reference — not the
 * full notification payload (title, template, variant data). Each `CustomNode`
 * fetches its notification details on mount and dispatches `ON_NODE_ENRICH` to
 * update the step in the reducer.
 *
 * The callbacks form a ref-counted semaphore so the builder knows when all async
 * fetches are complete. This is critical for snapshot-based dirty detection:
 * the initial "clean" snapshot must be captured *after* enrichment finishes,
 * otherwise the enrichment updates would make the journey appear dirty even
 * though the user hasn't changed anything.
 *
 * Flow:
 *  1. Builder loads → nodes render → each action node calls `onEnrichStart()`
 *  2. Notification fetch completes → `ON_NODE_ENRICH` dispatched → `onEnrichEnd()`
 *  3. When pending count hits 0 → `snapshotReady` flips → initial snapshot captured
 *  4. Future edits are compared against this post-enrichment snapshot
 */
export type EnrichmentCallbacks = {
    onEnrichStart: () => void
    onEnrichEnd: () => void
}

type JourneyContext = { state: JourneyState; dispatch: ActionDispatcher; enrichment?: EnrichmentCallbacks }
export const JourneyContext = createContext<JourneyContext>(null!)

export const useJourneyContext = (): JourneyReducerElements => {
    const { state, dispatch } = useContext(JourneyContext)
    return [state, dispatch]
}

export const useEnrichmentCallbacks = (): EnrichmentCallbacks | undefined => {
    const { enrichment } = useContext(JourneyContext)
    return enrichment
}
